import Icon from '../../components/common/Icon';
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import BackDeskLayout from '../../components/layout/BackDeskLayout';
import DevicePanel from '../../components/common/DevicePanel';
import RemoteDesktopModal from '../../components/common/RemoteDesktopModal';
import { useRealtime } from '../../hooks/useRealtime';
import { Button } from '../../components/ui';

const h = () => ({ Authorization: `Bearer ${localStorage.getItem('hd_token')}` });

function fmt(dt) {
  if (!dt) return '—';
  return new Date(dt).toLocaleString('es-ES', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' });
}

export default function ComputerDetail() {
  const { id }      = useParams();
  const navigate    = useNavigate();
  const [computer,     setComputer]     = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [script,       setScript]       = useState('');
  const [running,      setRunning]      = useState(false);
  const [result,       setResult]       = useState(null);
  const [toastTitle,   setToastTitle]   = useState('HelpDesk TI');
  const [toastMsg,     setToastMsg]     = useState('');
  const [toastSending, setToastSending] = useState(false);
  const [monitoring,   setMonitoring]   = useState(null);
  const [screenshot,   setScreenshot]   = useState(null);
  const [remoteActive, setRemoteActive] = useState(false);
  const [meshLoading,  setMeshLoading]  = useState(false);
  const [meshError,    setMeshError]    = useState('');
  const [showRemote,   setShowRemote]   = useState(false);
  const [filePath,     setFilePath]     = useState('C:\\Users\\Public\\Desktop\\');
  const [fileSending,  setFileSending]  = useState(false);

  useEffect(() => {
    fetch(`/api/computers/${id}`, { headers: h() })
      .then(r => r.json())
      .then(d => setComputer(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  const computerRef = useRef(null);
  useEffect(() => { computerRef.current = computer; }, [computer]);

  // SSE via singleton hook — no new EventSource per component
  useRealtime(useCallback((msg) => {
    const c = computerRef.current;
    if (!c) return;
    if (msg.type === 'computer_monitoring' && msg.hostname === c.hostname) {
      setMonitoring(msg);
    }
    if (msg.type === 'computer_screenshot' && msg.hostname === c.hostname) {
      setScreenshot(msg.data);
    }
  }, []));

  // On computer load: fetch cached monitoring + request fresh data from agent
  useEffect(() => {
    if (!computer) return;
    fetch(`/api/computers/${id}/monitoring`, { headers: h() })
      .then(r => r.json())
      .then(d => { if (d?.cpu_pct !== undefined) setMonitoring(d); })
      .catch(() => {});
    if (computer.online) {
      fetch(`/api/computers/${id}/monitoring/refresh`, { method: 'POST', headers: h() })
        .catch(() => {});
    }
  }, [computer?.hostname]);

  async function runScript() {
    if (!script.trim()) return;
    setRunning(true);
    setResult(null);
    try {
      const res = await fetch(`/api/computers/${id}/script`, {
        method: 'POST',
        headers: { ...h(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ script, interpreter: 'powershell' }),
      });
      const data = await res.json();
      if (data.ok) {
        setResult({ pending: true, job_id: data.job_id });
        // Escuchar resultado via SSE
        const token = localStorage.getItem('hd_token');
        const es = new EventSource(`/api/realtime/events?token=${token}`);
        es.onmessage = (e) => {
          const msg = JSON.parse(e.data);
          if (msg.type === 'script_result' && msg.job_id === data.job_id) {
            setResult(msg);
            setRunning(false);
            es.close();
          }
        };
        setTimeout(() => { es.close(); setRunning(false); }, 30000);
      } else {
        setResult({ error: data.error });
        setRunning(false);
      }
    } catch (e) {
      setResult({ error: e.message });
      setRunning(false);
    }
  }

  // Run a PS script and return stdout as a string (used by DevicePanel tabs)
  async function runScriptInline(script) {
    const res  = await fetch(`/api/computers/${id}/script`, {
      method: 'POST',
      headers: { ...h(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ script, interpreter: 'powershell' }),
    });
    const data = await res.json();
    if (!data.ok) throw new Error(data.error || 'Error al ejecutar el script');
    // Wait for result via SSE
    return new Promise((resolve, reject) => {
      const token = localStorage.getItem('hd_token');
      const es    = new EventSource(`/api/realtime/events?token=${token}`);
      const timer = setTimeout(() => { es.close(); reject(new Error('Timeout (30s)')); }, 30000);
      es.onmessage = (e) => {
        const msg = JSON.parse(e.data);
        if (msg.type === 'script_result' && msg.job_id === data.job_id) {
          clearTimeout(timer); es.close();
          if (msg.error) reject(new Error(msg.error));
          else resolve((msg.stdout || '').trim());
        }
      };
    });
  }

  async function sendToast() {
    if (!toastMsg.trim()) return;
    setToastSending(true);
    try {
      await fetch(`/api/computers/${id}/toast`, {
        method: 'POST',
        headers: { ...h(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: toastTitle, message: toastMsg }),
      });
      setToastMsg('');
    } finally { setToastSending(false); }
  }

  async function requestScreenshot() {
    await fetch(`/api/computers/${id}/screenshot`, { method: 'POST', headers: h() });
  }

  async function sendInput(type, data) {
    await fetch(`/api/computers/${id}/input`, {
      method: 'POST',
      headers: { ...h(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ type, ...data }),
    });
  }

  function sendFile(file) {
    setFileSending(true);
    const reader = new FileReader();
    reader.onload = async (e) => {
      const base64 = e.target.result.split(',')[1];
      await fetch(`/api/computers/${id}/file`, {
        method: 'POST',
        headers: { ...h(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ path: filePath + file.name, content: base64 }),
      });
      setFileSending(false);
    };
    reader.readAsDataURL(file);
  }

  if (loading) return <BackDeskLayout title="Equipo"><div className="bd-loading"><div className="bd-spinner-sm"/>Cargando...</div></BackDeskLayout>;
  if (!computer) return <BackDeskLayout title="Equipo"><div className="bd-empty">Equipo no encontrado</div></BackDeskLayout>;

  const inv = computer.inventory;

  return (
    <BackDeskLayout>

      <div style={{display:'grid',gridTemplateColumns:'1fr 320px',gap:20,alignItems:'start'}}>

        {/* Columna izquierda */}
        <div style={{display:'flex',flexDirection:'column',gap:16}}>

          {/* Info básica */}
          <div style={{background:'#fff',border:'1px solid #E5E7EB',borderRadius:12,padding:'20px 24px',boxShadow:'0 1px 3px rgba(0,0,0,.05)'}}>
            <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:16}}>
              <div style={{width:44,height:44,borderRadius:10,background:computer.online?'#F0FDF4':'#F9FAFB',border:`2px solid ${computer.online?'#86EFAC':'#E5E7EB'}`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:22}}>
                <Icon name="computer" size={24} style={{color:computer.online?'#15803D':'#9CA3AF'}} />
              </div>
              <div>
                <h2 style={{fontSize:18,fontWeight:700,color:'#111827'}}>{computer.hostname}</h2>
                <div style={{fontSize:12,color:computer.online?'#15803D':'#9CA3AF',fontWeight:600}}>
                  {computer.online ? 'En línea' : 'Fuera de línea'}
                  {computer.last_seen && ` · último contacto ${fmt(computer.last_seen)}`}
                </div>
              </div>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12}}>
              {[
                ['Usuario',   computer.username],
                ['IP Local',  computer.local_ip],
                ['OS',        computer.os],
                ['Sitio',     computer.site_name],
                ['Agente',    `v${computer.agent_version}`],
                ['Registrado',fmt(computer.first_seen)],
              ].map(([l,v]) => (
                <div key={l} style={{padding:'10px 12px',background:'#F9FAFB',borderRadius:8}}>
                  <div style={{fontSize:10,fontWeight:600,color:'#9CA3AF',textTransform:'uppercase',letterSpacing:.4,marginBottom:4}}>{l}</div>
                  <div style={{fontSize:13,fontWeight:500,color:'#111827'}}>{v||'—'}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Inventario + Monitorización (tabs) */}
          <div style={{background:'#fff',border:'1px solid #E5E7EB',borderRadius:12,padding:'20px 24px',boxShadow:'0 1px 3px rgba(0,0,0,.05)'}}>
            <div style={{fontSize:13,fontWeight:700,marginBottom:16}}>Inventario del equipo</div>
            <DevicePanel
              deviceInfo={inv?.hardware}
              inventory={inv}
              agentAvailable={computer.online}
              monitoring={monitoring}
              onAction={computer.online ? runScriptInline : null}
            />
          </div>

          {/* Ejecutar script */}
          {computer.online && (
            <div style={{background:'#fff',border:'1px solid #E5E7EB',borderRadius:12,padding:'20px 24px',boxShadow:'0 1px 3px rgba(0,0,0,.05)'}}>
              <div style={{fontSize:13,fontWeight:700,marginBottom:12}}>⚡ Ejecutar script PowerShell</div>
              <textarea
                value={script}
                onChange={e => setScript(e.target.value)}
                placeholder="Get-Process | Select-Object Name, CPU | Sort-Object CPU -Descending | Select-Object -First 10"
                rows={5}
                style={{width:'100%',padding:'10px 12px',border:'1.5px solid #E5E7EB',borderRadius:8,fontSize:12,fontFamily:'monospace',resize:'vertical',outline:'none',color:'#111827',boxSizing:'border-box'}}
              />
              <div style={{display:'flex',gap:8,marginTop:10}}>
                <Button variant="primary" onClick={runScript} disabled={running||!script.trim()}>
                  {running ? '⏳ Ejecutando...' : '▶ Ejecutar'}
                </Button>
                <Button variant="ghost" onClick={() => { setScript(''); setResult(null); }}>Limpiar</Button>
              </div>
              {result && (
                <div style={{marginTop:12,padding:'12px 14px',background:'#111827',borderRadius:8,fontFamily:'monospace',fontSize:12}}>
                  {result.pending
                    ? <span style={{color:'#FDE68A'}}>⏳ Esperando resultado...</span>
                    : result.error
                      ? <span style={{color:'#F87171'}}>⚠ {result.error}</span>
                      : <>
                          <pre style={{color:'#86EFAC',marginBottom:result.stderr?8:0,margin:0,whiteSpace:'pre-wrap',wordBreak:'break-word',fontFamily:'monospace',fontSize:12}}>{result.stdout || '(sin salida)'}</pre>
                          {result.stderr && <pre style={{color:'#F87171',margin:0,whiteSpace:'pre-wrap',wordBreak:'break-word',fontFamily:'monospace',fontSize:12}}>{result.stderr}</pre>}
                          <div style={{color:'#6B7280',marginTop:8,fontSize:11}}>Exit code: {result.exit_code}</div>
                        </>
                  }
                </div>
              )}
            </div>
          )}

        </div>

        {/* Columna derecha — acciones rápidas */}
        <div style={{display:'flex',flexDirection:'column',gap:12}}>
          <div style={{background:'#fff',border:'1px solid #E5E7EB',borderRadius:12,padding:'16px 18px',boxShadow:'0 1px 3px rgba(0,0,0,.05)'}}>
            <div style={{fontSize:12,fontWeight:700,marginBottom:12,color:'#111827'}}>Acciones rápidas</div>
            {/* Escritorio remoto — abre modal integrado */}
            <button
              onClick={() => setShowRemote(true)}
              style={{width:'100%',padding:'9px 14px',marginBottom:6,borderRadius:8,fontSize:12,fontWeight:500,
                textAlign:'left',cursor:'pointer',
                background:'#EDE9FE', border:'1px solid #DDD6FE', color:'#7C3AED',
                display:'flex',alignItems:'center',gap:8}}>
              <Icon name="desktop_windows" size={16} style={{color:'inherit',flexShrink:0}}/>
              Escritorio remoto
            </button>
            {[
              { label:'Forzar inventario', icon:'refresh', action: async () => {
                await fetch(`/api/computers/${id}/inventory`, { method:'POST', headers: h() });
                alert('Inventario solicitado. Se actualizará en unos segundos.');
              }},
              { label:'Reiniciar equipo', icon:'restart_alt', action: () => {
                if (confirm('¿Reiniciar el equipo?')) {
                  setScript('Restart-Computer -Force');
                  setTimeout(runScript, 100);
                }
              }},
              { label:'Ver procesos', icon:'settings', action: () => setScript('Get-Process | Sort-Object WorkingSet64 -Descending | Select-Object -First 20 | Format-Table Name, Id, @{N="RAM(MB)";E={[math]::Round($_.WorkingSet64/1MB,1)}} -AutoSize')},
              { label:'Espacio en disco', icon:'hard_drive', action: () => setScript('Get-PSDrive -PSProvider FileSystem | Format-Table Name, @{N="Total(GB)";E={[math]::Round($_.Used/1GB+$_.Free/1GB,1)}}, @{N="Free(GB)";E={[math]::Round($_.Free/1GB,1)}} -AutoSize')},
            ].map(a => (
              <button key={a.label}
                onClick={a.action}
                disabled={!computer.online}
                style={{width:'100%',padding:'9px 14px',marginBottom:6,borderRadius:8,fontSize:12,fontWeight:500,
                  textAlign:'left',cursor:computer.online?'pointer':'not-allowed',
                  background:computer.online?'#F9FAFB':'#F3F4F6',
                  border:'1px solid #E5E7EB',color:computer.online?'#374151':'#9CA3AF',
                  display:'flex',alignItems:'center',gap:8}}>
                {a.icon && <Icon name={a.icon} size={16} style={{color:'inherit',flexShrink:0}} />}
                {a.label}
              </button>
            ))}
            {!computer.online && (
              <div style={{fontSize:11,color:'#9CA3AF',marginTop:4,textAlign:'center'}}>
                Equipo fuera de línea — escritorio remoto disponible via Mesh
              </div>
            )}
          </div>
        </div>
      </div>

      {showRemote && (
        <RemoteDesktopModal computer={computer} onClose={() => setShowRemote(false)}/>
      )}
    </BackDeskLayout>
  );
}
